import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';

import type { PersonFieldsForm } from './personFields';

interface Props {
  /** §6.4's partial failure locks the person's fields — they are written (`AddCandidateDialog`). */
  disabled?: boolean;
}

/**
 * The four fields of a person, rendered once for the two dialogs that write them: §6.4's **Add
 * candidate** and §6.5's **Edit** (TASK-066). Labels, required marks, validation messages and order
 * live here, so the two forms cannot drift into asking for the same person in two different ways.
 *
 * Errors render **below their field** in `text-xs text-negative` through `Field`, never as a toast
 * and never through `alert` (§4 Forms) — a field error is a property of the field, and routing one
 * elsewhere is how somebody ends up hunting for which input went wrong.
 *
 * ⚠️ **It reads the form from context**, which is what lets one component serve two different form
 * types: §6.4's form is these four fields **plus the vacancy** and §6.5's is these four alone, so a
 * `UseFormReturn<T>` prop would not type against both. `useFormState()` (rather than
 * `useFormContext().formState`) is what subscribes *this* component to the error state, so a
 * message appears without the parent having to read the same field to make it re-render.
 */
export function PersonFields({ disabled = false }: Props) {
  const { t } = useTranslation('candidates');
  const { register } = useFormContext<PersonFieldsForm>();
  const { errors } = useFormState<PersonFieldsForm>();

  return (
    <>
      <Field
        label={t('dialog.fullName')}
        required
        error={errors.full_name ? t('dialog.fullNameRequired') : undefined}
      >
        {(fieldProps) => (
          <Input
            {...fieldProps}
            {...register('full_name')}
            autoComplete="off"
            disabled={disabled}
          />
        )}
      </Field>

      <Field label={t('dialog.email')} error={errors.email ? t('dialog.emailInvalid') : undefined}>
        {(fieldProps) => (
          <Input
            {...fieldProps}
            {...register('email')}
            type="email"
            autoComplete="off"
            disabled={disabled}
          />
        )}
      </Field>

      <Field label={t('dialog.phone')}>
        {(fieldProps) => (
          <Input
            {...fieldProps}
            {...register('phone')}
            type="tel"
            autoComplete="off"
            disabled={disabled}
          />
        )}
      </Field>

      <Field label={t('dialog.source')}>
        {(fieldProps) => (
          <Input {...fieldProps} {...register('source')} autoComplete="off" disabled={disabled} />
        )}
      </Field>
    </>
  );
}
